/* Fisher's Holiday 2026 — three tiny vanilla pieces, no theme dependencies:
   <fh-countdown> counts to data-deadline (ISO with offset) and flips data-state to "after";
   <fh-rotator> cycles announcement-bar slides (pauses on hover/focus; arrows stop autoplay);
   [data-fh-copy] buttons copy a promo code; postcard videos play only while on screen
   (never under prefers-reduced-motion). Loaded as a module, so it runs once per page. */

class FhCountdown extends HTMLElement {
  connectedCallback() {
    this.end = Date.parse(this.dataset.deadline || '');
    if (Number.isNaN(this.end)) return;
    this.out = this.querySelector('[data-fh-left]');
    this.tick();
    this.timer = setInterval(() => this.tick(), 30000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const ms = this.end - Date.now();
    if (ms <= 0) {
      this.dataset.state = 'after';
      clearInterval(this.timer);
      return;
    }
    this.dataset.state = 'live';
    const mins = Math.floor(ms / 60000);
    const d = Math.floor(mins / 1440);
    const h = Math.floor((mins % 1440) / 60);
    const m = mins % 60;
    const left = d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m` : `${Math.max(m, 1)}m`;
    if (this.out) this.out.textContent = `· ${left} left`;
  }
}

class FhRotator extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    if (!this.slides.length) return;
    this.index = 0;
    this.show(0);
    if (this.slides.length < 2) return;
    this.querySelector('[data-fh-prev]')?.addEventListener('click', () => this.step(-1, true));
    this.querySelector('[data-fh-next]')?.addEventListener('click', () => this.step(1, true));
    this.addEventListener('mouseenter', () => (this.paused = true));
    this.addEventListener('mouseleave', () => (this.paused = false));
    this.addEventListener('focusin', () => (this.paused = true));
    this.addEventListener('focusout', () => (this.paused = false));
    const seconds = Number(this.dataset.speed) || 6;
    this.timer = setInterval(() => !this.paused && this.step(1), seconds * 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  step(delta, manual = false) {
    if (manual) clearInterval(this.timer);
    this.show((this.index + delta + this.slides.length) % this.slides.length);
  }

  show(i) {
    this.index = i;
    this.slides.forEach((slide, n) => {
      slide.classList.toggle('is-active', n === i);
      slide.toggleAttribute('aria-hidden', n !== i);
    });
  }
}

if (!customElements.get('fh-countdown')) customElements.define('fh-countdown', FhCountdown);
if (!customElements.get('fh-rotator')) customElements.define('fh-rotator', FhRotator);

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-fh-copy]');
  if (!button) return;
  const code = button.dataset.fhCopy;
  try {
    await navigator.clipboard.writeText(code);
  } catch {
    const field = Object.assign(document.createElement('textarea'), { value: code });
    document.body.append(field);
    field.select();
    document.execCommand('copy');
    field.remove();
  }
  const label = button.querySelector('[data-fh-copy-label]');
  if (!label) return;
  const original = label.textContent;
  label.textContent = 'Copied!';
  setTimeout(() => (label.textContent = original), 2000);
});

/* Postcard videos: muted loops that play while visible. Reduced-motion visitors keep the poster frame;
   if the browser refuses playback (e.g. low-power mode) the poster simply stays. */
const fhVideos = document.querySelectorAll('.fh-postcard--video video');
if (fhVideos.length && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
  const play = (video) => {
    video.muted = true;
    video.play()?.catch(() => {});
  };
  if ('IntersectionObserver' in window) {
    const watcher = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => (isIntersecting ? play(target) : target.pause()));
    }, { threshold: 0.25 });
    fhVideos.forEach((video) => watcher.observe(video));
  } else {
    fhVideos.forEach(play);
  }
}
